import React, { useMemo } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { groupCartGstByRate } from "../../utils/gstTax";
import {
  billSeqDigits,
  resolveSaleBillDate,
} from "../../utils/billReceipt.helper";
import "./BillReceiptPreview.css";

export type BillReceiptItem = {
  productName?: string;
  name?: string;
  quantity: number;
  unitPrice?: number;
  netAmount?: number;
  totalPrice?: number;
  notes?: string;
  gstPercentage?: number;
  isTaxInclusive?: boolean;
};

export type BillReceiptSale = {
  saleNumber?: string;
  tableNumber?: string;
  orderType?: string;
  totalAmount?: number;
  discountAmount?: number;
  taxAmount?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  netAmount?: number;
  paymentMethod?: string;
  customerName?: string;
  cardHolder?: { holderName?: string };
  saleItems?: BillReceiptItem[];
  createdAt?: string;
  completedAt?: string;
  saleType?: string;
  storeName?: string;
  storeAddress?: string | null;
  storePhone?: string | null;
  storeGstin?: string | null;
  storeLicence?: string | null;
  storeMessage?: string | null;
  taxMode?: string | null;
  cashierName?: string;
};

function amountFmt(n: unknown) {
  return (Number(n) || 0).toFixed(2);
}

function money(sym: string, n: unknown) {
  return `${sym}${amountFmt(n)}`;
}

function formatReceiptDate(raw?: string | null) {
  const d = raw ? new Date(raw) : new Date();
  if (!raw || Number.isNaN(d.getTime())) {
    const now = new Date();
    return `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
  }
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function orderTypePreviewLabel(orderType?: string | null, isRandom?: boolean) {
  const t = String(orderType || "").toUpperCase();
  if (t === "DINE_IN") return "Dine In";
  if (t === "DELIVERY") return "Delivery";
  if (t === "TAKEAWAY" || t === "PICKUP") return "Pickup";
  if (isRandom) return "Pickup";
  return t || "Pickup";
}

function formatRateLabel(rate: number) {
  const n = Number(rate) || 0;
  if (Number.isInteger(n)) return `${n}%`;
  return `${n.toFixed(2).replace(/\.?0+$/, "")}%`;
}

function DividerLine() {
  return <div className="bill-receipt-rule">-------------------------------</div>;
}

type Props = {
  sale: BillReceiptSale;
  loading?: boolean;
  className?: string;
};

/** Thermal-style bill preview (matches Electron POS print layout). */
export default function BillReceiptPreview({ sale, loading, className }: Props) {
  const sym = "₹";
  const storeName = sale.storeName || "PayTool POS";
  const address = sale.storeAddress || "";
  const phone = sale.storePhone || "";
  const gst = sale.storeGstin || "";
  const fssai = sale.storeLicence || "";
  const message = sale.storeMessage || "";
  const taxMode = String(sale.taxMode || "CGST_SGST").toUpperCase();
  const cashier = sale.cashierName || "";
  const billNo = billSeqDigits(sale.saleNumber) || sale.saleNumber || "—";
  const dateLine = formatReceiptDate(resolveSaleBillDate(sale));
  const customer = sale.customerName || sale.cardHolder?.holderName || "";
  const payment = String(sale.paymentMethod || "cash").toUpperCase();
  const isRandom =
    sale.saleType === "random" ||
    String(sale.saleNumber || "")
      .toUpperCase()
      .startsWith("RND");
  const typeLabel = orderTypePreviewLabel(sale.orderType, isRandom);

  const items = useMemo(() => {
    return (sale.saleItems || []).map((item) => {
      const qty = Number(item.quantity) || 1;
      const unit = Number(item.unitPrice) || 0;
      const total = Number(item.netAmount ?? item.totalPrice) || unit * qty;
      return {
        name: item.productName || item.name || "Item",
        qty,
        unit: unit || (qty ? total / qty : 0),
        total,
        notes: item.notes,
        gstPercentage: Number(item.gstPercentage) || 0,
        isTaxInclusive: Boolean(item.isTaxInclusive),
      };
    });
  }, [sale.saleItems]);

  const totalQty = items.reduce((s, i) => s + i.qty, 0);
  const sub = Number(sale.totalAmount) || 0;
  const tax = Number(sale.taxAmount) || 0;
  const disc = Number(sale.discountAmount) || 0;
  const finalAmt = Number(sale.netAmount) || 0;

  const taxGroups = useMemo(
    () =>
      groupCartGstByRate(
        items.map((i) => ({
          lineNet: i.total,
          gstPercentage: i.gstPercentage,
          isTaxInclusive: i.isTaxInclusive,
          taxMode,
        }))
      ),
    [items, taxMode]
  );

  if (loading) {
    return (
      <Box className="bill-receipt-shell" sx={{ py: 6, textAlign: "center" }}>
        <CircularProgress size={22} />
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Loading bill…
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={`bill-receipt-shell ${className || ""}`.trim()}>
      <div className="bill-receipt-paper">
        <div className="bill-receipt-center bill-receipt-bold">PAID</div>
        <div className="bill-receipt-center bill-receipt-bold">{storeName}</div>
        {address ? (
          <div className="bill-receipt-center bill-receipt-bold">{address}</div>
        ) : null}
        {phone ? (
          <div className="bill-receipt-center bill-receipt-bold">
            Phone no- {phone}
          </div>
        ) : null}
        {gst ? (
          <div className="bill-receipt-center bill-receipt-bold">
            GSTIN- {gst}
          </div>
        ) : null}

        <DividerLine />

        <div className="bill-receipt-bold">NAME : {customer || "—"}</div>

        <DividerLine />

        <div>
          Date : {dateLine}   {typeLabel}
        </div>
        {cashier ? <div>Cashier : {cashier}</div> : null}
        <div>Bill No : {billNo}</div>
        {sale.tableNumber ? <div>Table : {sale.tableNumber}</div> : null}

        <DividerLine />

        {isRandom ? (
          <>
            <div className="bill-receipt-right">
              Amount: {money(sym, finalAmt || sale.netAmount)}
            </div>
            <div>Paid via {payment}</div>
          </>
        ) : items.length === 0 ? (
          <div className="bill-receipt-muted bill-receipt-center">
            No line items
          </div>
        ) : (
          <>
            <div className="bill-receipt-cols bill-receipt-bold">
              <span className="c-item">Item</span>
              <span className="c-qty">Qty</span>
              <span className="c-price">Price</span>
              <span className="c-amt">Amount</span>
            </div>
            <DividerLine />
            {items.map((item, idx) => (
              <div key={idx}>
                <div className="bill-receipt-cols">
                  <span className="c-item">{item.name}</span>
                  <span className="c-qty">{item.qty}</span>
                  <span className="c-price">{item.unit.toFixed(2)}</span>
                  <span className="c-amt">{item.total.toFixed(2)}</span>
                </div>
                {item.notes ? (
                  <div className="bill-receipt-muted"> {item.notes}</div>
                ) : null}
              </div>
            ))}
            <DividerLine />
            <div className="bill-receipt-totals-row">
              <span>Total Qty: {totalQty}</span>
              <span className="c-label">Sub Total</span>
              <span className="c-amt">{amountFmt(sub)}</span>
            </div>
            {taxGroups.length > 0
              ? taxGroups.map((g) => {
                  const rate = formatRateLabel(g.gstPercentage);
                  if (taxMode === "IGST") {
                    const amt = g.igstAmount || g.taxAmount;
                    return amt > 0 ? (
                      <div
                        key={`igst-${g.gstPercentage}`}
                        className="bill-receipt-totals-row"
                      >
                        <span />
                        <span className="c-label">IGST {rate}</span>
                        <span className="c-amt">{amountFmt(amt)}</span>
                      </div>
                    ) : null;
                  }
                  return (
                    <React.Fragment key={`gst-${g.gstPercentage}`}>
                      {g.cgstAmount > 0 ? (
                        <div className="bill-receipt-totals-row">
                          <span />
                          <span className="c-label">CGST {rate}</span>
                          <span className="c-amt">{amountFmt(g.cgstAmount)}</span>
                        </div>
                      ) : null}
                      {g.sgstAmount > 0 ? (
                        <div className="bill-receipt-totals-row">
                          <span />
                          <span className="c-label">SGST {rate}</span>
                          <span className="c-amt">{amountFmt(g.sgstAmount)}</span>
                        </div>
                      ) : null}
                      {g.cgstAmount <= 0 &&
                      g.sgstAmount <= 0 &&
                      g.taxAmount > 0 ? (
                        <div className="bill-receipt-totals-row">
                          <span />
                          <span className="c-label">Tax {rate}</span>
                          <span className="c-amt">{amountFmt(g.taxAmount)}</span>
                        </div>
                      ) : null}
                    </React.Fragment>
                  );
                })
              : tax > 0 ? (
                  <div className="bill-receipt-totals-row">
                    <span />
                    <span className="c-label">Tax</span>
                    <span className="c-amt">{amountFmt(tax)}</span>
                  </div>
                ) : null}
            {disc > 0 ? (
              <div className="bill-receipt-totals-row">
                <span />
                <span className="c-label">Discount</span>
                <span className="c-amt">{amountFmt(disc)}</span>
              </div>
            ) : null}
            <DividerLine />
            <div className="bill-receipt-row bill-receipt-bold bill-receipt-total">
              <span>Grand Total</span>
              <span className="bill-receipt-grand-amt">{money(sym, finalAmt)}</span>
            </div>
            <div>Paid via {payment}</div>
          </>
        )}

        <DividerLine />
        {fssai ? <div>FSSAI {fssai}</div> : null}
        <div className="bill-receipt-center">{message || "Thank you!"}</div>
      </div>
    </Box>
  );
}
